/**
 * AmbiguityCard body — "which did you mean?" disambiguator.
 *
 * Rendered when an agent (Enumerator or Scope) cannot narrow the user's
 * request to a single interpretation. Each candidate is a clickable
 * button. Clicking sends a synthetic text message back through the
 * conversation (via the `modify` ResolveAction that carries text),
 * which the parent intercepts and routes through postMessage instead
 * of resolveCard.
 *
 * No Confirm/Cancel footer — ambiguity cards have no plan status.
 * CardShell's footer is suppressed (planStatus: null).
 */
import React from 'react';
import { Button, Stack, Typography } from '@mui/material';

interface AmbiguityCandidate {
  label: string;
  payload: Record<string, unknown>;
  detail?: string | null;
}

interface AmbiguityCardBodyProps {
  content: Record<string, unknown>;
  onEditField: (field_updates: Record<string, unknown>) => void;
  editsDisabled: boolean;
}

const KIND_LABELS: Record<string, string> = {
  scenario: 'Which scenario?',
  tool_choice: 'Which capability?',
  forecast_model: 'Which forecast model?',
  forecast_model_instance: 'Which forecast model instance?',
  input: 'Which input?',
  output: 'Which output?',
  prior_task: 'Which prior task?',
  breadth: 'How broadly?',
};

export const AmbiguityCardBody: React.FC<AmbiguityCardBodyProps> = ({
  content,
  onEditField,
  editsDisabled,
}) => {
  const kind =
    typeof content.ambiguity_kind === 'string'
      ? content.ambiguity_kind
      : 'unknown';
  const candidates = Array.isArray(content.candidates)
    ? (content.candidates as AmbiguityCandidate[])
    : [];
  const heading = KIND_LABELS[kind] || 'Which did you mean?';

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
        {heading}
      </Typography>
      <Stack spacing={1}>
        {candidates.map((c, idx) => (
          <CandidateButton
            key={idx}
            candidate={c}
            kind={kind}
            disabled={editsDisabled}
            onSelect={() => {
              onEditField({ __ambiguity_select: c.label });
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

interface CandidateButtonProps {
  candidate: AmbiguityCandidate;
  kind: string;
  disabled: boolean;
  onSelect: () => void;
}

const CandidateButton: React.FC<CandidateButtonProps> = ({
  candidate,
  kind,
  disabled,
  onSelect,
}) => {
  return (
    <Button
      variant="outlined"
      size="small"
      disabled={disabled}
      onClick={onSelect}
      sx={{
        justifyContent: 'flex-start',
        textTransform: 'none',
        textAlign: 'left',
        px: 2,
        py: 1,
        borderColor: 'divider',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <Stack spacing={0.25} sx={{ width: '100%' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {candidate.label}
        </Typography>
        {candidate.detail ? (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {candidate.detail}
          </Typography>
        ) : null}
      </Stack>
    </Button>
  );
};
